/**
 * AUDIO MANAGER - Detectimáticas de 1º
 * Gestión de voces, síntesis de voz (TTS) y efectos de sonido.
 */

const AudioManager = {
    availableVoices: [],
    sounds: {
        pop: new Audio('sounds/pop.mp3'),
        success: new Audio('sounds/success.mp3'),
        error: new Audio('sounds/error.mp3'),
        confetti: new Audio('sounds/confetti.mp3')
    },
    config: {
        soundEnabled: true,
        volume: 0.7,
        useSystemVoiceForTitle: false // Reverted: ElevenLabs is preferred
    },
    hoverTimer: null,
    lastHoverElement: null,
    activeWatchdog: null,

    init() {
        console.log('🔊 Inicializando AudioManager...');

        // Cargar preferencia guardada
        const savedPref = localStorage.getItem('soundEnabled');
        if (savedPref !== null) {
            this.config.soundEnabled = savedPref === 'true';
        }

        this.synth = (typeof window !== 'undefined' && window.speechSynthesis) ? window.speechSynthesis : null;
        if (this.synth) {
            this.loadVoices();
            if (this.synth.onvoiceschanged !== undefined) {
                this.synth.onvoiceschanged = () => this.loadVoices();
            }
        } else {
            this.availableVoices = [];
        }

        // Hover-to-speak disabled as requested by user
        // this.setupHoverToSpeak();

        // Initialize SFX volume
        Object.values(this.sounds).forEach(s => s.volume = this.config.volume);

        // Resume AudioContext on first interaction (for some browsers)
        const resumeAudio = () => {
            if (this.synth && this.synth.paused) this.synth.resume();

            // Prime ElevenLabs audio player too
            if (window.ElevenLabsTTS && window.ElevenLabsTTS.audioPlayer) {
                window.ElevenLabsTTS.audioPlayer.play().catch(() => { });
                window.ElevenLabsTTS.audioPlayer.pause();
            }

            document.removeEventListener('click', resumeAudio);
        };
        document.addEventListener('click', resumeAudio);

        // Global Interaction Listener (SFX + Interruption)
        document.addEventListener('click', (e) => {
            const target = e.target;
            const isButton = target.closest('button, [role="button"], .btn, .selectable-card, .option-card, .context-card');

            if (isButton && this.config.soundEnabled) {
                this.playPop();
            }
        });
    },

    playSFX(name) {
        if (!this.config.soundEnabled) return;
        const sound = this.sounds[name];
        if (sound) {
            sound.currentTime = 0;
            sound.play().catch(() => {});
        }
    },

    // Direct aliases for compatibility
    playSuccess() { this.playSFX('success'); },
    playError() { this.playSFX('error'); },
    playPop() { this.playSFX('pop'); },
    playConfetti() { this.playSFX('confetti'); },

    loadVoices() {
        if (!this.synth) {
            this.availableVoices = [];
            return;
        }
        this.availableVoices = this.synth.getVoices() || [];
        console.log(`🔊 ${this.availableVoices.length} voces disponibles`);

        // Log de voces femeninas disponibles para debugging
        const femaleVoices = this.availableVoices.filter(voice =>
            voice.name.toLowerCase().includes('female') ||
            voice.name.toLowerCase().includes('mujer') ||
            voice.name.toLowerCase().includes('zira') ||
            voice.name.toLowerCase().includes('helena')
        );
        console.log('👧 Voces femeninas detectadas:', femaleVoices.map(v => v.name));
    },

    getBestVoice(langCode) {
        // Determinar si buscamos voz de niño o niña según el detective activo
        const isGirl = window.DetectiveMentor && window.DetectiveMentor.config.currentCharacter === 'girl';

        // Primero, intentar encontrar una voz que coincida exactamente con el idioma
        let voice = this.availableVoices.find(v => v.lang === langCode);

        // Si no hay coincidencia exacta, buscar por el código de idioma base (ej: 'es' de 'es-ES')
        if (!voice) {
            const baseLang = langCode.split('-')[0];
            voice = this.availableVoices.find(v => v.lang.startsWith(baseLang));
        }

        // Preferir SIEMPRE voces femeninas para mantener coherencia con los audios pregrabados
        const femaleKeywords = ['female', 'mujer', 'zira', 'helena', 'paulina', 'woman', 'sílvia', 'elsa'];
        
        const targetKeywords = femaleKeywords;

        const preferredVoice = this.availableVoices.find(v => {
            const matchLang = v.lang === langCode || v.lang.startsWith(langCode.split('-')[0]);
            const matchesGender = targetKeywords.some(keyword => v.name.toLowerCase().includes(keyword));
            return matchLang && matchesGender;
        });

        if (preferredVoice) {
            return preferredVoice;
        }

        // Fallback: si no encontramos el género ideal, intentamos la primera voz disponible en el idioma
        return preferredVoice || voice || this.availableVoices[0];
    },

    cleanText(text) {
        if (!text) return "";
        return text
            .replace(/<[^>]*>/g, ' ') // Strip HTML tags
            .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '')
            .replace(/[💡⭐✨🎉🎁🏆🕵️‍♀️🕵️‍♂️👦👧🏠🚪🔙←→✕▶🤖🌐🗺️📄✏️📊📖✏️🤔💡✕✓★🔊]|\n/gu, ' ')
            .trim()
            .replace(/\s+/g, ' ');
    },

    convertNumbersToWords(text, lang) {
        return text.replace(/\b\d+\b/g, match => {
            const num = parseInt(match, 10);
            if (isNaN(num) || num > 100) return match;
            
            if (lang.startsWith('gl')) {
                const units = ['cero', 'un', 'dous', 'tres', 'catro', 'cinco', 'seis', 'sete', 'oito', 'nove', 'dez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dezaseis', 'dezasete', 'dezaoito', 'dezanove', 'vinte', 'vinteún', 'vintedous', 'vintetrés', 'vintecatro', 'vintecinco', 'vinteseis', 'vintesete', 'vinteoito', 'vintenove'];
                const tens = ['', '', 'vinte', 'trinta', 'corenta', 'cincuenta', 'sesenta', 'setenta', 'oitenta', 'noventa'];
                if (num < 30) return units[num];
                if (num < 100) {
                    const t = Math.floor(num / 10);
                    const u = num % 10;
                    return u === 0 ? tens[t] : tens[t] + ' e ' + units[u];
                }
                if (num === 100) return 'cen';
            } else {
                const units = ['cero', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve', 'veinte', 'veintiuno', 'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve'];
                const tens = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
                if (num < 30) return units[num];
                if (num < 100) {
                    const t = Math.floor(num / 10);
                    const u = num % 10;
                    return u === 0 ? tens[t] : tens[t] + ' y ' + units[u];
                }
                if (num === 100) return 'cien';
            }
            return match;
        });
    },

    stop(excludeBtn = null) {
        // Clear any pending fallback watchdogs
        if (this.activeWatchdog) {
            clearTimeout(this.activeWatchdog);
            this.activeWatchdog = null;
        }

        if (window.ElevenLabsTTS) {
            window.ElevenLabsTTS.stop();
        }
        if (this.synth) {
            this.synth.cancel();
        }
        // Marcar todos los botones de audio como reseteados, excepto el excluido
        document.querySelectorAll('.audio-btn, .btn-audio-inline, .audio-enforced-btn, .btn-audio-narrative, .mentor-bubble-audio, .span-audio-enforced').forEach(btn => {
            if (btn !== excludeBtn) {
                this.resetAudioButton(btn);
            }
        });
    },

    speak(text, langCode, options = {}) {
        const lang = langCode || window.currentLanguage || 'es-ES';
        const cleanedText = this.cleanText(text);
        if (!cleanedText) return;

        // Almacenar el último texto hablado para que el mentor pueda repetirlo
        window.lastSpokenText = cleanedText;

        // Banned labels (Actions that should not have audio)
        const bannedLabels = [
            'CONTINUAR', 'COMENZAR LA AVENTURA', 'COMPROBAR', 'ENVIAR RESPUESTA',
            'VALIDAR REPRESENTACIÓN', 'VALIDAR SUBRAYADO', 'VALIDAR Y CONTINUAR',
            'SIGUIENTE', 'AVANZAR', 'VALIDAR RESULTADO', 'COMPROBAR TABLA'
        ];

        const upText = cleanedText.toUpperCase();
        const isBanned = bannedLabels.some(label => {
            // Check for exact match or exactly including icons
            return upText === label ||
                upText === `🔍 ${label}` ||
                upText === `${label} 🔍` ||
                upText === `<span>🔊</span> ${label}`.toUpperCase() ||
                upText === `<span>⏹️</span> ${label}`.toUpperCase();
        });

        if (isBanned) {
            console.log(`🔇 Blocked audio for action label: "${cleanedText}"`);
            return;
        }

        console.log(`🎙️ speak request: "${cleanedText.substring(0, 40)}${cleanedText.length > 40 ? '...' : ''}" (lang: ${lang})`);

        // Watchdog: If ElevenLabs is requested but fails/slows, fallback to native
        let watchdogTimer = null;

        // Detect if we are in a local environment (DEMO)
        const isShortText = cleanedText.length < 20;
        const isLocal = window.location.protocol === 'file:';

        // 1. Choice: High Quality (ElevenLabs / Local Files)
        // We removed !isShortText restriction to allow UI buttons (Suma, Resta, etc) to use high quality/local audio
        const isSpanish = lang.startsWith('es');
        if (!options.forceNative && isSpanish && window.ElevenLabsTTS && window.ElevenLabsTTS.useElevenLabs) {
            console.log(navigator.onLine ? '📡 Using ElevenLabs (Cloud/Local)...' : '🏠 Using ElevenLabs (Local Mode)...');

            // Start watchdog to ensure we speak even if API is slow or local fetch hangs
            if (this.activeWatchdog) clearTimeout(this.activeWatchdog);

            this.activeWatchdog = setTimeout(() => {
                if (!window.ElevenLabsTTS.isPlaying) {
                    console.warn('⚠️ ElevenLabs too slow/failed. Native fallback triggered.');
                    // IMPORTANT: Stop ElevenLabs before starting native to avoid double voice
                    if (window.ElevenLabsTTS) window.ElevenLabsTTS.stop();
                    this.activeWatchdog = null;
                    this.speak(cleanedText, lang, { forceNative: true, onEnd: options.onEnd });
                }
            }, 3500);

            window.ElevenLabsTTS.speak(cleanedText, lang, () => {
                if (this.activeWatchdog) {
                    clearTimeout(this.activeWatchdog);
                    this.activeWatchdog = null;
                }
                if (options.onEnd) options.onEnd();
            });
            return;
        }

        // 2. Standard Web Speech API (Native)
        console.log('🗣️ Using Web Speech API (Native Voice)...');
        if (!this.synth) this.synth = window.speechSynthesis;

        this.synth.cancel();
        if (this.synth.paused) this.synth.resume();

        // Convert numbers to words for native TTS to prevent English fallback issues on Windows
        let nativeText = cleanedText;
        nativeText = this.convertNumbersToWords(nativeText, lang);

        const utterance = new SpeechSynthesisUtterance(nativeText);
        const voiceConfig = (window.LANGUAGES && window.LANGUAGES[lang]) ? window.LANGUAGES[lang].voice : { rate: 0.85 };
        const voice = this.getBestVoice(lang);
        if (voice) utterance.voice = voice;

        utterance.pitch = 1.0; // Tono natural
        utterance.rate = voiceConfig.rate || 0.85;
        utterance.volume = 1.0;
        utterance.lang = lang;

        utterance.onend = options.onEnd;
        utterance.onerror = (e) => {
            console.error('❌ Native TTS Error:', e);
            if (options.onEnd) options.onEnd();
        };

        // Small delay to clear queue (Chrome fix)
        setTimeout(() => {
            if (this.synth && typeof this.synth.speak === 'function') {
                this.synth.speak(utterance);
            }
        }, 30);
    },

    speakWithCallback(text, langCode, onEndCallback = null, triggerBtn = null, options = {}) {
        const lang = langCode || window.currentLanguage || 'es-ES';
        const cleanedText = this.cleanText(text);
        if (!cleanedText) {
            if (typeof onEndCallback === 'function') onEndCallback();
            return;
        }

        // Almacenar el último texto hablado para que el mentor pueda repetirlo
        window.lastSpokenText = cleanedText;

        const bannedLabels = [
            'CONTINUAR', 'COMENZAR LA AVENTURA', 'COMPROBAR', 'ENVIAR RESPUESTA',
            'VALIDAR REPRESENTACIÓN', 'VALIDAR SUBRAYADO', 'VALIDAR Y CONTINUAR',
            'SIGUIENTE', 'AVANZAR', 'VALIDAR RESULTADO'
        ];

        const upText = cleanedText.toUpperCase();
        const isBanned = bannedLabels.some(label => {
            return upText === label ||
                upText === `🔍 ${label}` ||
                upText === `${label} 🔍` ||
                upText === `<span>🔊</span> ${label}`.toUpperCase() ||
                upText === `<span>⏹️</span> ${label}`.toUpperCase();
        });

        if (isBanned) {
            console.log(`🔇 Blocked audio for action label (callback mode): "${cleanedText}"`);
            if (typeof onEndCallback === 'function') onEndCallback();
            return;
        }

        console.log(`🎙️ speakWithCallback: "${cleanedText.substring(0, 40)}..."`);
        this.stop(triggerBtn);

        // 1. ElevenLabs Choice (Includes Local Files Priority)
        const isSpanish = lang.startsWith('es');
        if (!options.forceNative && isSpanish && window.ElevenLabsTTS && window.ElevenLabsTTS.useElevenLabs) {
            console.log('📡 speakWithCallback: Checking ElevenLabs/Local...');

            if (this.activeWatchdog) clearTimeout(this.activeWatchdog);

            this.activeWatchdog = setTimeout(() => {
                if (!window.ElevenLabsTTS.isPlaying) {
                    console.warn('⚠️ ElevenLabs too slow in Callback mode. Native fallback.');
                    if (window.ElevenLabsTTS) window.ElevenLabsTTS.stop();
                    this.activeWatchdog = null;
                    this.speakWithCallback(cleanedText, lang, onEndCallback, triggerBtn, { forceNative: true });
                }
            }, 3500);

            window.ElevenLabsTTS.speak(cleanedText, lang, () => {
                if (this.activeWatchdog) {
                    clearTimeout(this.activeWatchdog);
                    this.activeWatchdog = null;
                }
                if (typeof onEndCallback === 'function') onEndCallback();
            });
            return;
        }

        // 2. Native Choice
        console.log('🗣️ speakWithCallback: Using Native...');
        if (!this.synth) this.synth = window.speechSynthesis;

        this.synth.cancel();
        if (this.synth.paused) this.synth.resume();

        // Convert numbers to words for native TTS to prevent English fallback issues on Windows
        let nativeText = cleanedText;
        nativeText = this.convertNumbersToWords(nativeText, lang);

        const utterance = new SpeechSynthesisUtterance(nativeText);
        const voiceConfig = (window.LANGUAGES && window.LANGUAGES[lang]) ? window.LANGUAGES[lang].voice : { rate: 0.85 };
        const voice = this.getBestVoice(lang);
        if (voice) utterance.voice = voice;

        const isGirl = window.DetectiveMentor && window.DetectiveMentor.config.currentCharacter === 'girl';
        utterance.pitch = 1.0; // Tono natural
        utterance.rate = voiceConfig.rate || 0.85;
        utterance.volume = 1.0;
        utterance.lang = lang;

        utterance.onend = () => {
            if (typeof onEndCallback === 'function') onEndCallback();
        };

        utterance.onerror = (e) => {
            console.error('❌ Callback TTS Error:', e);
            if (typeof onEndCallback === 'function') onEndCallback();
        };

        setTimeout(() => {
            this.synth.speak(utterance);
        }, 50);
    },

    toggleAudio(btnElement, textOrParts) {
        if (!textOrParts || (Array.isArray(textOrParts) && textOrParts.length === 0)) return;

        // 1. Si ya está reproduciendo -> Detener y poner en "Mute" (estado detenido por el usuario)
        if (btnElement.classList.contains('playing')) {
            this.stop();

            btnElement.classList.remove('playing');
            btnElement.classList.remove('audio-stop-mode');
            btnElement.classList.add('muted-mode');

            const span = btnElement.querySelector('span') || (btnElement.tagName === 'SPAN' ? btnElement : null);
            if (span) {
                span.textContent = '🔇';
            } else {
                const existingText = btnElement.innerText.replace(/[🔊⏹️🔇]|\n/gu, '').trim();
                btnElement.innerHTML = `<span>🔇</span> ${existingText}`.trim();
            }

            // Estilo específico para el botón de narrativa si es necesario
            if (btnElement.classList.contains('btn-audio-narrative')) {
                btnElement.style.backgroundColor = '#f8fafc';
                btnElement.style.color = '#94a3b8';
                btnElement.style.borderColor = '#94a3b8';
            }
            return;
        }

        // 2. Si estaba en "Mute" o en "Ready" -> Reproducir desde el principio
        // Quitamos cualquier estado previo de este botón
        btnElement.classList.remove('muted-mode');

        // Resetear cualquier otro botón activo o muteado para que solo uno luzca activo/muteado a la vez
        document.querySelectorAll('.audio-btn.playing, .btn-audio-inline.playing, .muted-mode, .btn-audio-narrative.playing, .audio-enforced-btn.playing').forEach(btn => {
            if (btn !== btnElement) this.resetAudioButton(btn);
        });

        const isArray = Array.isArray(textOrParts);
        const fullText = isArray ? textOrParts.join(' ') : textOrParts;
        const wordCount = fullText.split(/\s+/).length;
        const isLongText = wordCount > 6;

        // Iniciar reproducción
        btnElement.classList.add('playing');
        const span = btnElement.querySelector('span') || (btnElement.tagName === 'SPAN' ? btnElement : null);
        if (span) {
            span.textContent = '⏹️';
        } else {
            const existingText = btnElement.innerText.replace(/[🔊⏹️🔇]|\n/gu, '').trim();
            btnElement.innerHTML = `<span>⏹️</span> ${existingText}`.trim();
        }

        // Estilo específico para el botón de narrativa (Azul mientras suena)
        if (btnElement.classList.contains('btn-audio-narrative')) {
            btnElement.style.backgroundColor = '#3b82f6';
            btnElement.style.color = 'white';
            btnElement.style.borderColor = '#2563eb';
        }

        if (isLongText) {
            btnElement.classList.add('audio-stop-mode');
        }

        const onFinish = () => {
            // Si terminó naturalmente, lo reseteamos al estado inicial listo (🔊)
            this.resetAudioButton(btnElement);
        };

        if (isArray) {
            this.speakParts(textOrParts, window.currentLanguage, onFinish);
        } else {
            this.speakWithCallback(textOrParts, window.currentLanguage, onFinish, btnElement);
        }
    },

    speakParts(parts, langCode, onEndCallback = null) {
        const lang = langCode || window.currentLanguage || 'es-ES';
        if (window.ElevenLabsTTS && window.ElevenLabsTTS.useElevenLabs) {
            window.ElevenLabsTTS.speakParts(parts, lang, onEndCallback);
        } else {
            // Fallback for native: play as a single sentence
            this.speakWithCallback(parts.join('. '), lang, onEndCallback);
        }
    },

    setupHoverToSpeak() {
        // Disabled by user request: no more hover-to-speak
        return;
    },

    playAudioForHoveredElement(el) {
        const audioBtn = el.querySelector('.audio-btn, .btn-audio-inline') ||
            (el.classList.contains('audio-btn') || el.classList.contains('btn-audio-inline') ? el : null);

        if (audioBtn && !audioBtn.classList.contains('playing')) {
            audioBtn.click();
            return;
        }

        let text = "";
        if (el.classList.contains('option-card')) {
            const title = el.querySelector('.option-title')?.innerText || "";
            const desc = el.querySelector('.option-desc')?.innerText || "";
            const isLevelCard = el.closest('#levelGrid') !== null;
            text = isLevelCard ? title : `${title}. ${desc}`;
        } else if (el.classList.contains('context-card')) {
            const title = el.querySelector('.context-title')?.innerText || "";
            const desc = el.querySelector('.context-description')?.innerText || "";
            text = `${title}. ${desc}`;
        } else if (el.classList.contains('category-header')) {
            text = el.querySelector('.category-title')?.innerText || "";
        } else if (el.classList.contains('step-indicator')) {
            const num = el.querySelector('.step-number')?.innerText?.trim() || "";
            const label = el.querySelector('.step-label')?.innerText || "";
            
            // Map numbers to words to force correct language pronunciation
            const lang = window.currentLanguage || 'es-ES';
            let wordNum = num;
            
            if (lang.startsWith('gl')) {
                const galicianNums = { '1': 'un', '2': 'dous', '3': 'tres', '4': 'catro', '5': 'cinco', '6': 'seis' };
                if (galicianNums[num]) wordNum = galicianNums[num];
            } else {
                const spanishNums = { '1': 'uno', '2': 'dos', '3': 'tres', '4': 'cuatro', '5': 'cinco', '6': 'seis' };
                if (spanishNums[num]) wordNum = spanishNums[num];
            }
            
            text = `Paso ${wordNum}, ${label}`;
        } else {
            text = el.innerText.replace(/[←→✕▶🤖🌐🗺️📄✏️📊📖✏️🤔💡✕✓]|\n/gu, ' ').trim();
        }

        if (text && text.length > 0 && text.length < 200) {
            console.log(`🎙️ Hover-to-speak: "${text}"`);
            this.speak(text, window.currentLanguage);
        }
    },

    resetAudioButton(btn) {
        if (!btn) return;
        btn.classList.remove('playing');
        btn.classList.remove('audio-stop-mode');
        btn.classList.remove('muted-mode');

        // Intentar separar el texto si existe
        const span = btn.querySelector('span') || (btn.tagName === 'SPAN' ? btn : null);
        if (span) {
            span.textContent = '🔊';
        } else {
            // Preserve existing text if any
            const existingText = btn.innerText.replace(/[🔊⏹️🔇]|\n/gu, '').trim();
            btn.innerHTML = `<span>🔊</span> ${existingText}`.trim();
        }

        // Restaurar estilos de narrativa si corresponde
        if (btn.classList.contains('btn-audio-narrative')) {
            const contextColor = btn.getAttribute('data-context-color') || '#3b82f6';
            btn.style.backgroundColor = 'white';
            btn.style.color = contextColor;
            btn.style.borderColor = contextColor;
        }
    },

    addSafeAudioButtons() {
        const textSelectors = [
            'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'label',
            '.wizard-step-instruction', '.instruction-text', '.reasoning-intro p',
            '.contextual-prompt', '.narrative-text', '#statementText',
            '.step-navigation-message p', '.comprehension-question', '.reasoning-substep p',
            '.reasoning-substep label', '.substep-intro', '.substep-question-reminder',
            '.wizard-step p', '.summary-message p', '.error-help-message',
            '.error-help-tip', '.step-instruction', '.error-message',
            '.error-hint', '.quick-answer-label', '.help-text', '.help-bubble p',
            '.swal2-content', '.method-title', '.method-header',
            '.option-title', '.context-title', '.category-title'
        ].join(', ');

        const isExcluded = (el) => {
            const text = (el.innerText || '').toUpperCase();
            // Exclusion keywords for Parents/Teachers/Admin parts
            const forbiddenKeys = ['PADRE', 'PARENT', 'PROFE', 'TEACHER', 'EDIT', 'SALIR', 'EXIT', 'SWORK', '©', 'VOLVER', 'SIGUIENTE', 'ANTERIOR', 'AYUDA'];
            if (forbiddenKeys.some(k => text.includes(k))) return true;
            if (el.id === 'helpBtn' || el.id === 'helpHighlightBtn' || el.classList.contains('btn-help')) return true;
            if (el.closest('#parentsDashboardScreen') || el.closest('.editor-tools') || el.classList.contains('btn-edit') ||
                el.closest('#problemHelpModalV2') || el.closest('#guideScreen') || el.closest('#detectiveModal') ||
                el.closest('#narrativeScreen') || el.closest('#numericTableScreen') || el.closest('#workBoardModal') ||
                el.closest('#loginScreen') || el.closest('#splashScreen') || el.id === 'mainTitle') return true;
            return false;
        };

        const getCleanWordCount = (text) => {
            if (!text) return 0;
            // Remove icons and emojis to get real word count
            const clean = text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, ' ')
                .replace(/[💡⭐✨🎉🎁🏆🕵️‍♀️🕵️‍♂️👦👧🏠🚪🔙←→✕▶🤖🌐🗺️📄✏️📊📖🤔✓★🔊\n\r\t]/gu, ' ')
                .trim();
            return clean.split(/\s+/).filter(w => w.length > 0).length;
        };

        // 1. Process Text Elements
        document.querySelectorAll(textSelectors).forEach(container => {
            // Already has any type of audio button?
            if (container.querySelector('.audio-btn, .btn-audio-inline, .audio-enforced-btn, .auto-enforced, .span-audio-enforced, .mentor-bubble-audio')) return;
            // Hidden or blocked?
            if (container.offsetParent === null || container.classList.contains('no-audio')) return;
            if (isExcluded(container)) return;

            let text = container.innerText.trim();
            
            if (container.classList.contains('method-header')) {
                const title = container.querySelector('.method-title')?.innerText?.trim();
                if (title) text = title;
            }

            const words = getCleanWordCount(text);

            // Critical elements get audio button even if short (1+ word)
            const isCritical = container.closest('.wizard-step-instruction, .instruction-text, #statementText, .error-help-message, .help-step, .narrative-text, .option-card, .context-card, .category-header, label, .substep-intro, .substep-question-reminder');
            if (words < (isCritical ? 1 : 3)) return;
            if (text === 'Cargando problema...' || text === '') return;

            const btn = document.createElement('button');
            btn.className = 'btn-audio-inline auto-enforced';
            btn.type = 'button';
            btn.innerHTML = '<span>🔊</span>';
            btn.style.marginRight = '8px';
            btn.style.verticalAlign = 'middle';
            btn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleAudio(btn, text);
            };

            if (container.firstChild) {
                container.insertBefore(btn, container.firstChild);
                container.insertBefore(document.createTextNode(' '), btn.nextSibling);
            } else {
                container.appendChild(btn);
            }
        });

        // 2. Process Buttons (Add internal speaker if long text)
        document.querySelectorAll('button, .btn').forEach(btn => {
            // Skip audio buttons themselves
            if (btn.classList.contains('btn-audio-inline') || btn.classList.contains('audio-btn') || btn.classList.contains('audio-enforced-btn')) return;
            // Hidden, blocked or already has speaker inside?
            if (btn.offsetParent === null || btn.classList.contains('no-audio')) return;
            if (isExcluded(btn)) return;
            if (btn.querySelector('.audio-btn, .btn-audio-inline, .audio-enforced-btn, .auto-enforced, .span-audio-enforced')) return;

            const text = btn.innerText.trim();
            let cleanText = text.replace(/[🔊⏹️🔇]/gu, '').trim();

            if (btn.classList.contains('step-indicator')) {
                const label = btn.querySelector('.step-label')?.innerText?.trim();
                if (label) cleanText = label;
            } else if (btn.classList.contains('method-card')) {
                const title = btn.querySelector('.method-card-title')?.innerText?.trim();
                if (title) cleanText = title;
            }

            if (getCleanWordCount(cleanText) < 1) return; // Permitir botones cortos como Suma, Resta, Pregunta

            const trigger = document.createElement('span');
            trigger.className = 'span-audio-enforced';
            trigger.innerHTML = '🔊';
            trigger.style.cssText = 'margin-left:8px; cursor:pointer; font-size:1.1em; display:inline-block; vertical-align:middle;';

            trigger.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleAudio(trigger, cleanText);
            };

            btn.appendChild(trigger);
        });
    }
};

// Aliases para mantener compatibilidad con el código existente
window.speak = (text, lang, options = {}) => AudioManager.speak(text, lang, options);
window.speakWithCallback = (text, lang, callback, options = {}) => AudioManager.speakWithCallback(text, lang, callback, null, options);
window.speakText = (text, options = {}) => AudioManager.speak(text, window.currentLanguage, options);
window.cleanTextForSpeech = (text) => AudioManager.cleanText(text);
window.toggleAudio = (btn, text) => AudioManager.toggleAudio(btn, text);
window.stopAudio = () => AudioManager.stop();
window.addSafeAudioButtons = () => AudioManager.addSafeAudioButtons();
window.setupHoverToSpeak = () => AudioManager.setupHoverToSpeak();

// SFX Aliases
window.playPop = () => AudioManager.playSFX('pop');
window.playSuccess = () => AudioManager.playSFX('success');
window.playError = () => AudioManager.playSFX('error');
window.playConfetti = () => AudioManager.playSFX('confetti');

// Global Manager Aliases
window.AudioService = AudioManager;
window.SoundService = AudioManager;
window.AudioManager = AudioManager;
window.useSystemVoiceForTitle = AudioManager.config.useSystemVoiceForTitle;

// Funciones específicas de la app que antes estaban en app.js
window.playWelcomeMessage = (force = false) => {
    // Evitar múltiples reproducciones automáticas, pero permitir si se fuerza (ej: tras login)
    if (!force && window.welcomeAudioPlayed) return;

    // Use a smaller delay if forced by user action (e.g. login click)
    const delay = force ? 100 : 1000;

    // Delay playback to allow transitions and voices to load
    setTimeout(() => {
        const triggerAudio = () => {
            // Si ya está hablando o ya ha sonado (y no es forzado), cancelamos
            const isSpeakingNatively = window.speechSynthesis && window.speechSynthesis.speaking;
            const isSpeakingEleven = window.ElevenLabsTTS && window.ElevenLabsTTS.isPlaying;

            if (!force && (window.welcomeAudioPlayed || isSpeakingNatively || isSpeakingEleven)) return;

            const lang = window.currentLanguage || 'es-ES';
            const strings = (window.LANGUAGES && window.LANGUAGES[lang]) ? window.LANGUAGES[lang].strings : null;

            // Buscar título de la splash screen primero, luego el mainTitle tradicional
            const splashTitle = document.querySelector('#splashScreen.active .main-title');
            const title = splashTitle ? splashTitle.textContent :
                (document.getElementById('mainTitle') ? document.getElementById('mainTitle').textContent :
                    (strings ? strings.title : 'Detectimáticas de 1º'));

            console.log('📣 Reproduciendo audio de bienvenida:', title);
            window.welcomeAudioPlayed = true;
            window.speak(title, lang, { forceNative: window.useSystemVoiceForTitle });
        };

        // Retry once if voices are not yet loaded
        if (AudioManager.availableVoices.length === 0) {
            setTimeout(triggerAudio, 500);
        } else {
            triggerAudio();
        }

        // Safety fallback: if audio was blocked, try again on first click
        const safetyTrigger = () => {
            // Solo disparamos si no ha sonado ya y no está hablando
            const isSpeakingNatively = window.speechSynthesis && window.speechSynthesis.speaking;
            const isSpeakingEleven = window.ElevenLabsTTS && window.ElevenLabsTTS.isPlaying;

            if (!window.welcomeAudioPlayed && !isSpeakingNatively && !isSpeakingEleven) {
                triggerAudio();
            }
            document.removeEventListener('click', safetyTrigger);
        };
        document.addEventListener('click', safetyTrigger, { once: true });
    }, delay);
};

window.speakProblemTypeTitle = () => {
    const btn = document.getElementById('btnAudioProblemTypeTitle');
    const textEl = document.getElementById('problemTypeTitleText');
    const strings = (window.LANGUAGES && window.LANGUAGES[window.currentLanguage]) ? window.LANGUAGES[window.currentLanguage].strings : {};
    const title = strings.problemTypeTitle || 'Investigaré problemas de';

    if (btn && textEl && window.toggleAudio) {
        const text = textEl.textContent.replace(/^\d+\.\s*/, '').trim();
        window.toggleAudio(btn, text || title);
    } else {
        window.speak(title, window.currentLanguage);
    }
};

window.speakLevelTitle = () => {
    const btn = document.getElementById('btnAudioLevelTitle');
    const textEl = document.getElementById('levelTitleText');
    const strings = (window.LANGUAGES && window.LANGUAGES[window.currentLanguage]) ? window.LANGUAGES[window.currentLanguage].strings : {};
    const title = strings.levelTitle || '¿Qué clase de investigador eres?';

    if (btn && textEl && window.toggleAudio) {
        const text = textEl.textContent.replace(/^\d+\.\s*/, '').trim();
        window.toggleAudio(btn, text || title);
    } else {
        window.speak(title, window.currentLanguage);
    }
};

// Aliases globales
window.AudioManager = AudioManager;
window.stopAudio = (excludeBtn) => AudioManager.stop(excludeBtn);
window.speak = (...args) => AudioManager.speak(...args);
window.speakWithCallback = (...args) => AudioManager.speakWithCallback(...args);
window.toggleAudio = (...args) => AudioManager.toggleAudio(...args);

// Inicialización automática
AudioManager.init();
